import React, { useEffect, useRef, useState } from 'react'
import styles from './HeroSection.module.css'

/* Swap with real client logos. */
const CLIENT_LOGOS = [
  { src: 'https://pub-f369b33409cf4a599883d7bec60d1538.r2.dev/logos/Godrej%20Industries%20Black.png', alt: 'Client 1' },
  { src: 'https://pub-f369b33409cf4a599883d7bec60d1538.r2.dev/logos/Screenshot%202026-07-13%20145301-Photoroom.png', alt: 'Client 2' },
  { src: 'https://pub-f369b33409cf4a599883d7bec60d1538.r2.dev/logos/Volkswagen_-_Logo.svg.webp', alt: 'Client 3' },
  { src: 'https://pub-f369b33409cf4a599883d7bec60d1538.r2.dev/logos/image-Photoroom.png', alt: 'Client 4' },
  { src: 'https://pub-f369b33409cf4a599883d7bec60d1538.r2.dev/logos/netsurf.png', alt: 'Client 5' },
]

const STATS = [
  { label: 'Events', end: 50, suffix: '+', detail: 'Delivered across India' },
  { label: 'Cities', end: 12, suffix: '', detail: 'And counting' },
  { label: 'Attendees', end: 8, suffix: 'K+', detail: 'Engaged, not just seated' },
  { label: 'Retention', end: 98, suffix: '%', detail: 'Clients who come back' },
]

/* Counts from 0 → end once the element scrolls into view. */
const CountUp = ({ end, suffix, duration = 1400 }) => {
  const ref = useRef(null)
  const [value, setValue] = useState(0)
  const started = useRef(false)

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduceMotion) {
      setValue(end)
      return
    }

    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return
        started.current = true

        const t0 = performance.now()
        const tick = (now) => {
          const p = Math.min((now - t0) / duration, 1)
          const eased = 1 - Math.pow(1 - p, 3) // ease-out cubic
          setValue(Math.round(eased * end))
          if (p < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
        observer.disconnect()
      },
      { threshold: 0.4 }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [end, duration])

  return (
    <span ref={ref}>
      {value}
      {suffix}
    </span>
  )
}

const HeroSection = ({ logos = CLIENT_LOGOS, stats = STATS }) => {
  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <div className={styles.layoutGrid}>

          {/* --- Left Column: Header & Context --- */}
          <div className={styles.headerColumn}>
            <div className={styles.eyebrowWrapper}>
              <span className={styles.eyebrowLine} />
              <p className={styles.eyebrow}>Impact in Numbers</p>
            </div>

            <h2 className={styles.heading}>
              Great events speak for themselves.<br />
              <span className={styles.headingInk}>We&rsquo;ll keep this brief.</span>
            </h2>

            <p className={styles.subtitle}>
              Partnering with forward-thinking teams to engineer seamless, high-impact corporate experiences that leave a lasting impression.
            </p>
          </div>

          {/* --- Right Column: Bento Stat Ledger --- */}
          <div className={styles.ledger}>
            {stats.map((stat) => (
              <div key={stat.label} className={styles.statCard}>
                <div className={styles.statHeader}>
                  <span className={styles.statNum}>
                    <CountUp end={stat.end} suffix={stat.suffix} />
                  </span>
                  <span className={styles.statLabel}>{stat.label}</span>
                </div>
                <p className={styles.statDetail}>{stat.detail}</p>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* --- Logo marquee (full-bleed) --- */}
      <div className={styles.marqueeWrap}>
        <p className={styles.marqueeLabel}>Trusted by industry leaders</p>
        <div className={styles.marquee} aria-hidden={false}>
          <div className={styles.marqueeTrack}>
            {[...logos, ...logos, ...logos].map((logo, i) => (
              <img
                key={i}
                src={logo.src}
                alt={i < logos.length ? logo.alt : ''}
                aria-hidden={i >= logos.length}
                className={styles.logo}
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection